/** @jsxImportSource @emotion/react */

import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { Form } from 'react-final-form'
import { CircularProgress } from '@mui/material'
import { MenuSelectField } from 'components/Select/MenuSelectField'
import { PlatformSlug } from 'constants/consts'

import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { InputField } from 'components/Input'
import TextareaAutosize from 'components/TextareaAutosize/TextareaAutosize'
import { validateRequired } from 'utils/validation'
import { FieldValues } from 'utils/types'
import { MembershipLevelSelect, getMembershipAccessItems } from 'components/MembershipLevelSelect'
import { toast } from 'utils/toast'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { RadioField } from 'components/Radio'
import { MembershipType } from '__generatedTypes__/globalTypes'
import * as classes from 'components/shared/GroupCards/commonStyles'
import { BOOSTER_ENV_TYPE, NoteEnvironmentOption } from './noteEnvironment'

const isPdfProcessingInProgressError = (error: Error) =>
  error.message?.startsWith("OpenAI didn't process the file yet") ||
  error.message?.startsWith('Failed to delete old file from OpenAI')

const isInvalidLinkError = (error: Error) => error.message?.startsWith('Invalid Google Drive link')

const isInvalidTypeError = (error: Error) => error.message?.startsWith('Invalid file type:')

const handleErrorMessage = (error: Error, fallbackMessage: string) => {
  if (isPdfProcessingInProgressError(error)) {
    toast.error('The previous PDF is still being processed. Please try again in a moment.')
    return
  }

  if (isInvalidLinkError(error)) {
    toast.error('URL should be a valid Google Drive link')
    return
  }

  if (isInvalidTypeError(error)) {
    toast.error('Invalid file type: Only PDF files are supported. The uploaded file is not a PDF')
    return
  }

  toast.error(fallbackMessage)
}

type Group = {
  id: string
  title: string
}

type Card = {
  id: string
  title?: string
  name?: string
  description: string
  link: string
  isBoosterEnvironment: boolean
  membershipLevel?: string
}

export type FormValues = {
  id: string
  name: string
  description: string
  link: string
  group: {
    id: string
  }
  membershipLevel: MembershipType
  noteEnvironment: { id: string }
  landingPageDescription?: string
}

export type UpdateCardData = FormValues & { isDocumentUpdated?: boolean; isBoosterEnvironmentUpd?: boolean }

const getInitialValues = (
  initialValues: FieldValues,
  platform?: PlatformSlug,
  noteEnvironmentItems: NoteEnvironmentOption[] = []
): FieldValues => {
  const membershipAccessItems = getMembershipAccessItems(platform)
  const singleMembershipLevel = membershipAccessItems.length === 1 ? membershipAccessItems[0].id : undefined

  return {
    ...initialValues,
    membershipLevel: singleMembershipLevel ?? initialValues.membershipLevel,
    noteEnvironment: noteEnvironmentItems.length === 1 ? noteEnvironmentItems[0] : initialValues.noteEnvironment,
  }
}

type Props<CardType extends Card> = {
  card: CardType
  groups: Group[]
  isEditMode: boolean
  isLoading: boolean
  title: string
  initialValues: FieldValues
  onGroupRemove: () => void
  onClose: () => void
  updateCard: (data: Partial<UpdateCardData>) => Promise<void>
  createCard: (data: FieldValues) => Promise<void>
  noteEnvironmentItems: NoteEnvironmentOption[]
  hasLandingPage?: boolean
}

export const URLCardForm = <CardType extends Card>({
  title,
  onGroupRemove,
  groups,
  onClose,
  isEditMode,
  card,
  initialValues,
  updateCard,
  createCard,
  isLoading,
  noteEnvironmentItems,
  hasLandingPage = false,
}: Props<CardType>) => {
  const { selectedPlatformSlug } = usePlatformsAndSubjects({})

  const descriptionLabel = hasLandingPage ? 'Classroom Description' : 'Description'

  const handleForcePdfUpdate = async () => {
    if (!isEditMode || !card.isBoosterEnvironment) {
      return
    }

    try {
      await updateCard({
        id: card.id,
        link: card.link,
        isDocumentUpdated: true,
      })

      toast.success('PDF updated successfully')
    } catch (error) {
      if (isPdfProcessingInProgressError(error as Error)) {
        toast.error('The previous PDF is still being processed. Please try again in a moment.')
        return
      }

      toast.error('Failed to update PDF')
    }
  }

  const handleSubmit = async (data: FieldValues) => {
    if (isEditMode) {
      try {
        const isBoosterEnvironmentPrev = card.isBoosterEnvironment
        const isBoosterEnvironmentUpd = data.noteEnvironment?.id === BOOSTER_ENV_TYPE.id
        const isDocumentUpdated =
          data.isDocumentUpdated !== undefined
            ? data.isDocumentUpdated
            : isBoosterEnvironmentUpd && !isBoosterEnvironmentPrev

        await updateCard({
          ...data,
          isDocumentUpdated,
          isBoosterEnvironmentUpd,
        })
        onClose()
        toast.success('Card updated successfully')
      } catch (error) {
        handleErrorMessage(error as Error, 'Failed to update card')
      }
    } else {
      try {
        await createCard(data)
        onClose()
        toast.success('Card created successfully')
      } catch (error) {
        handleErrorMessage(error as Error, 'Failed to create card')
      }
    }
  }

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      initialValues={getInitialValues(initialValues, selectedPlatformSlug, noteEnvironmentItems)}
      render={({ handleSubmit, values }) => (
        <form css={classes.modalForm} onSubmit={handleSubmit}>
          <Typography css={classes.modalTitle} variant="title2">
            {title}
          </Typography>
          {noteEnvironmentItems.length > 1 && (
            <RadioField
              name="noteEnvironment"
              label="Note Environment"
              items={noteEnvironmentItems}
              validate={validateRequired}
              displayErrorIfUntouched
              row
            />
          )}
          <InputField name="name" validate={validateRequired} label="Card Title" fullWidth />
          <TextareaAutosize
            value={values.description}
            label={descriptionLabel}
            validate={validateRequired}
            css={classes.descriptionInput}
            name="description"
          />
          {hasLandingPage && (
            <TextareaAutosize
              value={values.landingPageDescription}
              label="Landing Page Description"
              validate={validateRequired}
              css={classes.descriptionInput}
              name="landingPageDescription"
            />
          )}
          <Typography variant="body2" css={classes.label}>
            Group
          </Typography>
          <MenuSelectField css={classes.select} fullWidth name="group.id" required>
            {groups.map((group) => (
              <MenuItem value={group.id} key={group.id}>
                <div>{group.title}</div>
              </MenuItem>
            ))}
          </MenuSelectField>
          <InputField name="link" validate={validateRequired} label="URL" fullWidth />
          {isEditMode &&
            card.isBoosterEnvironment &&
            values.noteEnvironment?.id === BOOSTER_ENV_TYPE.id &&
            card.link === values.link && (
              <Button
                onClick={handleForcePdfUpdate}
                css={[classes.button, classes.fullButton]}
                variant="contained"
                type="button"
                color="blue"
                size="large"
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Force PDF update'}
              </Button>
            )}
          <MembershipLevelSelect
            name="membershipLevel"
            validate={validateRequired}
            fullWidth
            hideWhenSingleOption
            platform={selectedPlatformSlug}
          />
          <div css={classes.buttons}>
            {isEditMode && (
              <Button css={classes.button} variant="outlined" color="red" size="large" onClick={onGroupRemove}>
                <TrashIcon />
                Delete
              </Button>
            )}
            <Button css={classes.button} variant="outlined" color="blue" size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              css={[classes.button, isEditMode && classes.fullButton]}
              variant="contained"
              type="submit"
              color="blue"
              size="large"
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={16} color="inherit" /> : 'Submit'}
            </Button>
          </div>
        </form>
      )}
    />
  )
}
